import React, { useCallback, useEffect, useRef, useState } from 'react';
import './tmi.css';
import './Packlists.css';
import './MasterProjects.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridColumn,
    GridFilterChangeEvent,
    GridItemChangeEvent,
    GridCellProps,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import IconPickerDialog from './IconPickerDialog';
import { isWebComponentsPrivateUseCode, parseIconCode, toIconEntity } from './iconCodeUtils';

const MENU_STRUCTURE_CHANGED_EVENT = 'tmi:menus-structure-changed';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type MenusProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: MenusState;
    onSaveState?: (menuItemId: number, state: MenusState) => void;
};

const PRIMARY_COLUMN_FIELDS = [
    'parentSecurityID',
    'securityActionTypeID',
    'sortOrder',
    'url',
    'packListTypeID',
    'iconCode',
    'mobileEnable',
    'browserEnable',
] as const;

const SECONDARY_COLUMN_FIELDS = [
    'enable',
    'tabletEnable',
    'showEmptyPackList',
    'showNACells',
] as const;

const isColumnVisible = (field: string, showSecondaryColumns: boolean): boolean => {
    if (PRIMARY_COLUMN_FIELDS.includes(field as typeof PRIMARY_COLUMN_FIELDS[number])) {
        return !showSecondaryColumns;
    }

    if (SECONDARY_COLUMN_FIELDS.includes(field as typeof SECONDARY_COLUMN_FIELDS[number])) {
        return showSecondaryColumns;
    }

    return true;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface MenuInfoDto {
    clientRowKey: string;
    securityID: number;
    parentSecurityID: number | null;
    securityLabel: string;
    description: string;
    url: string;
    sortOrder: number | null;
    enable: boolean;
    actionTypeId: number|null;
    packListTypeID: number | null;
    iconCode: string;
    iconImage: string | null;
    securityActionTypeID: number;
    mobileEnable: boolean;
    tabletEnable: boolean;
    browserEnable: boolean;
    showEmptyPackList: boolean;
    showNACells: boolean;
    lastChangedBy: string;
    lastChangedOn: Date | null;
}

interface UserRoleMenuRow {
    clientRowKey: string;
    SecurityClientQueryID: number;
    SecurityID: number;
    ClientQueryID: number | null;
    SortOrder: number;
    MultiSelect: boolean;
    LastChangedBy: string;
    LastChangedOn: Date | null;
    display: string;
    inEdit?: boolean;
}

interface MenusState {
    data: MenuInfoDto[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filter: FilterDescriptor[];
    filterText: string;
    filterTexts: { [key: string]: string };
    showSecondaryColumns: boolean;
    milestoneTypesDropdown: LookupListItem[];
    actionTypesDropdown: LookupListItem[];
    headerFocusField: string | null;
    editRowKey: string | null;
    editViewOrderKeys: string[] | null;
    originalItem: MenuInfoDto | null;
    selectedIds: Set<number>;
    deleteConfirmItem: MenuInfoDto | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    newDialogOpen: boolean;
    newDialogSaving: boolean;
    newDialogError: string;
    newDialogSecurityID: string;
    newDialogParentSecurityID: string;
    newDialogSecurityLabel: string;
    newDialogDescription: string;
    newDialogURL: string;
    newDialogSortOrder: string;
    newDialogEnable: boolean;
    newDialogPackListTypeID: string;
    newDialogActionTypeID: string;
    newDialogIconCode: string;
    newDialogMobileEnable: boolean;
    newDialogTabletEnable: boolean;
    newDialogBrowserEnable: boolean;
    newDialogShowEmptyPackList: boolean;
    newDialogShowNACells: boolean;
    iconPickerOpen: boolean;
    iconPickerContext: 'edit' | 'new' | null;
    iconPickerValue: string;
    menuDataItem: MenuInfoDto | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    allowedItemTypesDlgOpen: boolean;
    allowedItemTypesMilestone: MenuInfoDto | null;
    allowedItemTypesDropdownData: LookupListItem[];
    allowedItemTypesSelected: Set<string>;
    allowedItemTypesDlgLoading: boolean;
    allowedItemTypesDlgError: string;
    allowedItemTypesDlgFilterText: string;
    userRolesDlgOpen: boolean;
    userRolesMilestone: MenuInfoDto | null;
    userRolesDropdownData: LookupListItem[];
    userRolesData: UserRoleMenuRow[];
    userRolesDlgLoading: boolean;
    userRolesDlgError: string;
    userRolesEditingKey: string | null;
    userRolesOriginalItem: UserRoleMenuRow | null;
    userRolesDeleteConfirmKey: string | null;
    userRolesDeleteInProgress: boolean;
    userRolesDeleteError: string;
}

const normalizeMenu = (item: any, index: number): MenuInfoDto => {
    const securityIDRaw = item.securityID ?? item.SecurityID ?? item.milestoneID ?? item.MilestoneID ?? 0;
    const securityIDParsed = Number(securityIDRaw);
    const securityID = Number.isFinite(securityIDParsed) ? securityIDParsed : 0;
    const securityLabel = item.securityLabel ?? item.SecurityLabel ?? item.milestoneCode ?? item.MilestoneCode ?? '';
    const description = item.description ?? item.Description ?? item.milestoneDesc ?? item.MilestoneDesc ?? '';
    const url = item.url ?? item.URL ?? item.milestoneAltDesc ?? item.MilestoneAltDesc ?? '';
    const sortOrder = item.sortOrder ?? item.SortOrder ?? item.milestoneOrder ?? item.MilestoneOrder ?? 1;
    const packListTypeID = item.packListTypeID ?? item.PackListTypeID ?? item.milestoneTypeID ?? item.MilestoneTypeID ?? null;
    const mobileEnable = item.mobileEnable ?? item.MobileEnable ?? item.allowMultiScan ?? item.AllowMultiScan ?? false;
    const tabletEnable = item.tabletEnable ?? item.TabletEnable ?? item.doNotCascadeScan ?? item.DoNotCascadeScan ?? false;
    const browserEnable = item.browserEnable ?? item.BrowserEnable ?? item.fillFromParts ?? item.FillFromParts ?? false;
    const showEmptyPackList = item.showEmptyPackList ?? item.ShowEmptyPackList ?? item.fillItemsFromPackLists ?? item.FillItemsFromPackLists ?? false;
    const showNACells = item.showNACells ?? item.ShowNACells ?? item.partialChildrenWeight ?? item.PartialChildrenWeight ?? false;
    const enable = item.enable ?? item.Enable ?? item.alwaysEditMilestoneLines ?? item.AlwaysEditMilestoneLines ?? false;
    const lastChangedOnRaw = item.lastChangedOn ?? item.LastChangedOn;

    const parentSecurityIDRaw = item.parentSecurityID ?? item.ParentSecurityID ?? item.backfillFromMilestoneID ?? item.BackfillFromMilestoneID ?? null;
    const parentSecurityIDParsed = parentSecurityIDRaw === null || parentSecurityIDRaw === '' ? null : Number(parentSecurityIDRaw);
    const parentSecurityID = parentSecurityIDParsed === null || !Number.isFinite(parentSecurityIDParsed)
        ? null
        : parentSecurityIDParsed;

    const resolvedActionTypeIdRaw = item.securityActionTypeID
        ?? item.SecurityActionTypeID
        ?? item.securityActionTypeId
        ?? item.SecurityActionTypeId
        ?? item.actionTypeId
        ?? item.ActionTypeId
        ?? null;
    const resolvedActionTypeId = resolvedActionTypeIdRaw === null || resolvedActionTypeIdRaw === ''
        ? null
        : Number(resolvedActionTypeIdRaw);

    return {
        clientRowKey: `${securityID}_${index}`,
        securityID,
        parentSecurityID,
        securityLabel,
        description,
        url,
        sortOrder,
        enable,
        actionTypeId: resolvedActionTypeId,
        packListTypeID: packListTypeID === null || packListTypeID === '' ? null : Number(packListTypeID),
        iconCode: item.iconCode ?? item.IconCode ?? '',
        iconImage: item.iconImage ?? item.IconImage ?? null,
        securityActionTypeID: Number.isFinite(resolvedActionTypeId) ? Number(resolvedActionTypeId) : 0,
        mobileEnable,
        tabletEnable,
        browserEnable,
        showEmptyPackList,
        showNACells,
        lastChangedBy: item.lastChangedBy ?? item.LastChangedBy ?? '',
        lastChangedOn: lastChangedOnRaw ? new Date(lastChangedOnRaw) : null,
    };
};

const applyMenuFilters = (rows: MenuInfoDto[], filterTexts: { [key: string]: string }): MenuInfoDto[] => {
    if (Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)) {
        return rows;
    }

    return rows.filter(item => {
        return Object.entries(filterTexts).every(([f, q]) => {
            if (!q) return true;
            const val = (item as any)[f];
            const ql = q.trim().toLowerCase();
            if (ql === 'true' || ql === 'false') {
                return String(val ?? '').toLowerCase() === ql;
            }
            return String(val ?? '').toLowerCase().includes(ql);
        });
    });
};

const Menus: React.FC<MenusProps> = (props) => {
    const EDIT_FIELD = 'inEdit';

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters/sort/dialogs) via stateToLoad/onSaveState,
    // so the page doesn't need to refetch/refilter/reselect. On first visit there is no
    // stateToLoad, so fall back to the defaults below.
    const [state, setState] = useState<MenusState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: [{ field: 'sortOrder', dir: 'asc' }],
        filter: [],
        filterText: '',
        filterTexts: {},
        showSecondaryColumns: false,
        milestoneTypesDropdown: [],
        actionTypesDropdown: [],
        headerFocusField: null,
        editRowKey: null,
        editViewOrderKeys: null,
        originalItem: null,
        selectedIds: new Set<number>(),
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        newDialogOpen: false,
        newDialogSaving: false,
        newDialogError: '',
        newDialogSecurityID: '0',
        newDialogParentSecurityID: '',
        newDialogSecurityLabel: '',
        newDialogDescription: '',
        newDialogURL: '',
        newDialogSortOrder: '1',
        newDialogEnable: false,
        newDialogPackListTypeID: '',
        newDialogActionTypeID: '',
        newDialogIconCode: '',
        newDialogMobileEnable: false,
        newDialogTabletEnable: false,
        newDialogBrowserEnable: false,
        newDialogShowEmptyPackList: false,
        newDialogShowNACells: false,
        iconPickerOpen: false,
        iconPickerContext: null,
        iconPickerValue: '',
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        allowedItemTypesDlgOpen: false,
        allowedItemTypesMilestone: null,
        allowedItemTypesDropdownData: [],
        allowedItemTypesSelected: new Set<string>(),
        allowedItemTypesDlgLoading: false,
        allowedItemTypesDlgError: '',
        allowedItemTypesDlgFilterText: '',
        userRolesDlgOpen: false,
        userRolesMilestone: null,
        userRolesDropdownData: [],
        userRolesData: [],
        userRolesDlgLoading: false,
        userRolesDlgError: '',
        userRolesEditingKey: null,
        userRolesOriginalItem: null,
        userRolesDeleteConfirmKey: null,
        userRolesDeleteInProgress: false,
        userRolesDeleteError: '',
    });

    const stateRef = useRef<MenusState>(state);
    stateRef.current = state;
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuBlurTimeoutRef = useRef<number | null>(null);
    const userInfRef = useRef(props.userInf);
    const updateUserInfoRef = useRef(props.updateUserInfo);

    const keepEditInputFocus = useCallback((rowKey: string, field: string, selectionStart: number | null, selectionEnd: number | null) => {
        requestAnimationFrame(() => {
            const selector = `input[data-edit-cell="${rowKey}::${field}"]`;
            const el = document.querySelector(selector) as HTMLInputElement | null;
            if (!el) return;
            el.focus();
            if (selectionStart !== null && selectionEnd !== null) {
                try {
                    el.setSelectionRange(selectionStart, selectionEnd);
                } catch {
                    // Ignore non-text input selection errors.
                }
            }
        });
    }, []);

    useEffect(() => {
        userInfRef.current = props.userInf;
    }, [props.userInf]);

    useEffect(() => {
        updateUserInfoRef.current = props.updateUserInfo;
    }, [props.updateUserInfo]);

    const fetchData = useCallback(() => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMenus';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({
                            ...prev,
                            data: prev.editRowKey !== null
                                ? prev.data
                                : (Array.isArray(res.results) ? res.results.map((item: any, index: number) => normalizeMenu(item, index)) : []),
                            loading: false,
                        }));
                        break;
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                        updateUserInfoRef.current(uInf);
                        setState(prev => ({ ...prev, loading: false }));
                        break;
                    }
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching menus: ' + (res.callStatusMessage || ''), loading: false }));
                }
            })
            .catch(err => {
                    setState(prev => ({ ...prev, errorMsg: 'Error fetching menus: ' + err.toString(), loading: false }));
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    const fetchPacklistTypesDropdown = useCallback(() => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                    updateUserInfoRef.current(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, milestoneTypesDropdown: res.results }));
                }
            })
            .catch(() => {
                // Do not block page usage when dropdown data is unavailable.
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    const fetchActionTypesDropdown = useCallback(() => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetActionTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                    updateUserInfoRef.current(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    const options: LookupListItem[] = res.results
                        .map((item: any) => ({
                            value: String(item.value ?? item.id ?? item.securityActionTypeID ?? item.SecurityActionTypeID ?? ''),
                            display: String(item.display ?? item.name ?? item.description ?? item.Description ?? ''),
                        }))
                        .filter((opt: LookupListItem) => opt.value !== '');

                    setState(prev => ({ ...prev, actionTypesDropdown: options }));
                }
            })
            .catch(() => {
                // Do not block page usage when action type dropdown is unavailable.
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    useEffect(() => {
        fetchData();
        fetchPacklistTypesDropdown();
        fetchActionTypesDropdown();
        return () => {
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
        };
    }, [fetchData, fetchPacklistTypesDropdown, fetchActionTypesDropdown]);

    const wouldCreateCircularParentReference = useCallback((
        securityId: number,
        candidateParentSecurityId: number | null,
        rows: MenuInfoDto[]
    ): boolean => {
        if (candidateParentSecurityId === null) {
            return false;
        }

        if (candidateParentSecurityId === securityId) {
            return true;
        }

        const parentById = new Map<number, number | null>(
            rows.map(row => [row.securityID, row.parentSecurityID])
        );

        const visited = new Set<number>();
        let currentParentId: number | null = candidateParentSecurityId;
        while (currentParentId !== null) {
            if (currentParentId === securityId) {
                return true;
            }
            if (visited.has(currentParentId)) {
                // Existing cycle in data; block this assignment to avoid compounding it.
                return true;
            }
            visited.add(currentParentId);
            currentParentId = parentById.get(currentParentId) ?? null;
        }

        return false;
    }, []);

    const handleFilterChange = useCallback((e: GridFilterChangeEvent) => {
        setState(prev => ({ ...prev, filter: [] }));
    }, []);

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, filterTexts: { ...prev.filterTexts, [field]: val } }));
    }, []);

    const handleItemChange = (e: GridItemChangeEvent) => {
        const edited = e.dataItem as MenuInfoDto;
        setState(prev => ({
            ...prev,
            data: prev.data.map(item => item.clientRowKey === edited.clientRowKey ? edited : item),
        }));
    };

    const handleEnterEdit = (dataItem: MenuInfoDto) => {
        setState(prev => {
            const filtered = applyMenuFilters(prev.data, prev.filterTexts || {});
            const visibleSorted = orderBy(filtered, prev.sort);
            return {
                ...prev,
                editRowKey: dataItem.clientRowKey,
                editViewOrderKeys: visibleSorted.map(item => item.clientRowKey),
                originalItem: { ...dataItem },
            };
        });
    };

    const handleCancel = () => {
        if (!state.originalItem) return;
        const origItem = state.originalItem;
        setState(prev => ({
            ...prev,
            data: prev.data.map(item => item.clientRowKey === origItem.clientRowKey ? origItem : item),
            editRowKey: null,
            editViewOrderKeys: null,
            originalItem: null,
            iconPickerOpen: false,
            iconPickerContext: null,
            iconPickerValue: '',
        }));
    };

    const handleSave = (dataItem: MenuInfoDto) => {
        const sortOrder = Number(dataItem.sortOrder ?? 0);
        if (!Number.isInteger(sortOrder)) {
            setState(prev => ({ ...prev, errorMsg: 'Sort Order must be a whole number.' }));
            return;
        }

        if (wouldCreateCircularParentReference(dataItem.securityID, dataItem.parentSecurityID, state.data)) {
            setState(prev => ({ ...prev, errorMsg: 'Parent Security selection would create a circular reference.' }));
            return;
        }

        setState(prev => ({ ...prev, loading: true }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMenu';

        const payload = {
            SecurityID: dataItem.securityID,
            ParentSecurityID: dataItem.parentSecurityID,
            SecurityLabel: dataItem.securityLabel,
            Description: dataItem.description,
            URL: dataItem.url,
            SortOrder: sortOrder,
            Enable: dataItem.enable,
            PackListTypeID: dataItem.packListTypeID,
            IconCode: dataItem.iconCode,
            IconImage: dataItem.iconImage,
            SecurityActionTypeID: dataItem.securityActionTypeID,
            MobileEnable: dataItem.mobileEnable,
            TabletEnable: dataItem.tabletEnable,
            BrowserEnable: dataItem.browserEnable,
            ShowEmptyPackList: dataItem.showEmptyPackList,
            ShowNACells: dataItem.showNACells,
            LastChangedBy: dataItem.lastChangedBy,
            LastChangedOn: dataItem.lastChangedOn,
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, loading: false }));
                } else if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        editRowKey: null,
                        editViewOrderKeys: null,
                        originalItem: null,
                        loading: false,
                        iconPickerOpen: false,
                        iconPickerContext: null,
                        iconPickerValue: '',
                    }));
                    window.dispatchEvent(new Event(MENU_STRUCTURE_CHANGED_EVENT));
                    fetchData();
                } else {
                    setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving menu: ' + (res.callStatusMessage || '') }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving menu: ' + err.toString() }));
            });
    };

    const isProtectedTmiMenuLabel = (label: string | null | undefined): boolean => {
        return (label || '').trim().toLowerCase().startsWith('tmi');
    };

    const openDeleteConfirm = (item: MenuInfoDto) => {
        if (isProtectedTmiMenuLabel(item.securityLabel)) {
            setState(prev => ({
                ...prev,
                deleteConfirmItem: null,
                deleteErrorMsg: '',
                errorMsg: "Menus with labels starting with 'TMI' cannot be deleted.",
            }));
            return;
        }
        setState(prev => ({ ...prev, deleteConfirmItem: item, deleteErrorMsg: '' }));
    };

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
    };

    const confirmDelete = () => {
        if (!state.deleteConfirmItem) {
            return;
        }

        if (isProtectedTmiMenuLabel(state.deleteConfirmItem.securityLabel)) {
            setState(prev => ({
                ...prev,
                deleteConfirmItem: null,
                deleteInProgress: false,
                deleteErrorMsg: '',
                errorMsg: "Menus with labels starting with 'TMI' cannot be deleted.",
            }));
            return;
        }

        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteMenu';
        const securityID = state.deleteConfirmItem.securityID;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(securityID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, deleteInProgress: false }));
                } else if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        deleteConfirmItem: null,
                        deleteInProgress: false,
                        deleteErrorMsg: '',
                    }));
                    window.dispatchEvent(new Event(MENU_STRUCTURE_CHANGED_EVENT));
                    fetchData();
                } else {
                    setState(prev => ({
                        ...prev,
                        deleteInProgress: false,
                        deleteErrorMsg: res.callStatusMessage || 'Menu could not be deleted.',
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    deleteInProgress: false,
                    deleteErrorMsg: 'Error deleting menu: ' + err.toString(),
                }));
            });
    };

    const openNewDialog = () => {
        setState(prev => ({
            ...prev,
            newDialogOpen: true,
            newDialogSaving: false,
            newDialogError: '',
            newDialogSecurityID: '0',
            newDialogParentSecurityID: '',
            newDialogSecurityLabel: '',
            newDialogDescription: '',
            newDialogURL: '',
            newDialogSortOrder: '1',
            newDialogEnable: false,
            newDialogPackListTypeID: '',
            newDialogActionTypeID: '',
            newDialogIconCode: '',
            newDialogMobileEnable: false,
            newDialogTabletEnable: false,
            newDialogBrowserEnable: false,
            newDialogShowEmptyPackList: false,
            newDialogShowNACells: false,
        }));
    };

    const closeNewDialog = () => {
        setState(prev => ({
            ...prev,
            newDialogOpen: false,
            newDialogSaving: false,
            newDialogError: '',
            iconPickerOpen: false,
            iconPickerContext: null,
            iconPickerValue: '',
        }));
    };

    const openIconPicker = useCallback((context: 'edit' | 'new', value: string) => {
        setState(prev => ({
            ...prev,
            iconPickerOpen: true,
            iconPickerContext: context,
            iconPickerValue: value,
        }));
    }, []);

    const closeIconPicker = useCallback(() => {
        setState(prev => ({
            ...prev,
            iconPickerOpen: false,
            iconPickerContext: null,
            iconPickerValue: '',
        }));
    }, []);

    const applyIconPickerSelection = useCallback((selectedCode: number | null) => {
        const nextValue = selectedCode === null ? '' : toIconEntity(selectedCode);

        setState(prev => {
            if (prev.iconPickerContext === 'new') {
                return {
                    ...prev,
                    newDialogIconCode: nextValue,
                    iconPickerOpen: false,
                    iconPickerContext: null,
                    iconPickerValue: '',
                };
            }

            if (prev.iconPickerContext === 'edit' && prev.editRowKey) {
                return {
                    ...prev,
                    data: prev.data.map(item => item.clientRowKey === prev.editRowKey ? { ...item, iconCode: nextValue } : item),
                    iconPickerOpen: false,
                    iconPickerContext: null,
                    iconPickerValue: '',
                };
            }

            return {
                ...prev,
                iconPickerOpen: false,
                iconPickerContext: null,
                iconPickerValue: '',
            };
        });
    }, []);

    const toggleColumnVisibility = useCallback(() => {
        setState(prev => ({ ...prev, showSecondaryColumns: !prev.showSecondaryColumns }));
    }, []);

    const submitNew = () => {
        const sortOrderValue = state.newDialogSortOrder.trim();
        const sortOrder = Number(sortOrderValue);
        if (!sortOrderValue || !Number.isInteger(sortOrder)) {
            setState(prev => ({ ...prev, newDialogError: 'Sort Order is required and must be a whole number.' }));
            return;
        }

        const parentSecurityValue = state.newDialogParentSecurityID.trim();
        const parentSecurityID = parentSecurityValue === '' ? null : Number(parentSecurityValue);
        if (
            parentSecurityValue !== ''
            && (
                parentSecurityID === null
                || !Number.isInteger(parentSecurityID)
                || parentSecurityID < 0
            )
        ) {
            setState(prev => ({ ...prev, newDialogError: 'Parent Security ID must be a valid whole number.' }));
            return;
        }

        const packListTypeIDValue = state.newDialogPackListTypeID.trim();
        const packListTypeID = packListTypeIDValue === '' ? null : Number(packListTypeIDValue);
        if (packListTypeIDValue !== '' && !Number.isInteger(packListTypeID)) {
            setState(prev => ({ ...prev, newDialogError: 'PackList Type must be a valid whole number.' }));
            return;
        }

        const actionTypeIDValue = state.newDialogActionTypeID.trim();
        const actionTypeID = actionTypeIDValue === '' ? null : Number(actionTypeIDValue);
        if (actionTypeIDValue !== '' && !Number.isInteger(actionTypeID)) {
            setState(prev => ({ ...prev, newDialogError: 'Action Type must be a valid whole number.' }));
            return;
        }

        setState(prev => ({ ...prev, newDialogSaving: true, newDialogError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMenu';

        const payload = {
            SecurityID: 0,
            ParentSecurityID: parentSecurityID,
            SecurityLabel: state.newDialogSecurityLabel.trim(),
            Description: state.newDialogDescription.trim(),
            URL: state.newDialogURL.trim(),
            SortOrder: sortOrder,
            Enable: state.newDialogEnable,
            PackListTypeID: packListTypeID,
            ActionTypeID: actionTypeID,
            IconCode: state.newDialogIconCode.trim(),
            IconImage: null,
            SecurityActionTypeID: actionTypeID ?? 0,
            MobileEnable: state.newDialogMobileEnable,
            TabletEnable: state.newDialogTabletEnable,
            BrowserEnable: state.newDialogBrowserEnable,
            ShowEmptyPackList: state.newDialogShowEmptyPackList,
            ShowNACells: state.newDialogShowNACells,
            LastChangedBy: props.userInf.displayName || '',
            LastChangedOn: null,
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newDialogSaving: false }));
                } else if (res.callStatus === 'OK') {
                    window.dispatchEvent(new Event(MENU_STRUCTURE_CHANGED_EVENT));
                    closeNewDialog();
                    fetchData();
                } else {
                    setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: res.callStatusMessage || 'Error creating menu.' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: 'Error creating menu: ' + err.toString() }));
            });
    };

    const openRowMenu = (dataItem: MenuInfoDto, anchor: HTMLElement | null) => {
        const actionTypeDisplay = state.actionTypesDropdown
            .find(actionType => Number(actionType.value) === Number(dataItem.securityActionTypeID))
            ?.display
            ?.trim()
            .toLowerCase();

        if (actionTypeDisplay !== 'spl search') {
            closeRowMenu();
            return;
        }

        if (anchor) {
            const anchorRect = anchor.getBoundingClientRect();
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } else {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
    };

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null, menuAnchorRect: null, menuPosition: null }));
    };

    const handleMenuBlur = () => {
        if (menuBlurTimeoutRef.current) {
            window.clearTimeout(menuBlurTimeoutRef.current);
        }
        menuBlurTimeoutRef.current = window.setTimeout(() => {
            closeRowMenu();
        }, 120);
    };

    const openAllowedItemTypesDialog = (milestone: MenuInfoDto) => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: true,
            allowedItemTypesMilestone: milestone,
            allowedItemTypesDropdownData: [],
            allowedItemTypesSelected: new Set<string>(),
            allowedItemTypesDlgLoading: true,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
        }));

        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetSecurityItemTypes?SecurityID=${milestone.securityID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetItemTypesForDropDown`;

        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                if (dataRes.callStatus === 'UNAUTH' || dropdownRes.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, allowedItemTypesDlgLoading: false }));
                    return;
                }

                let dropdownData: LookupListItem[] = [];
                let selectedIds = new Set<string>();
                let hasError = false;

                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                } else {
                    hasError = true;
                }

                if (dataRes.callStatus === 'OK' && Array.isArray(dataRes.results)) {
                    selectedIds = new Set(
                        (dataRes.results as any[])
                            .map((item: any) => {
                                if (typeof item === 'number' || typeof item === 'string') return String(item);
                                if (!item || typeof item !== 'object') return '';
                                return String(
                                    item.itemTypeID
                                    ?? item.ItemTypeID
                                    ?? item.securityItemTypeID
                                    ?? item.SecurityItemTypeID
                                    ?? item.allowedItemTypeID
                                    ?? item.AllowedItemTypeID
                                    ?? item.id
                                    ?? item.ID
                                    ?? ''
                                );
                            })
                            .filter((id: string) => id !== '')
                    );
                } else {
                    hasError = true;
                }

                setState(prev => ({
                    ...prev,
                    allowedItemTypesDropdownData: dropdownData,
                    allowedItemTypesSelected: selectedIds,
                    allowedItemTypesDlgLoading: false,
                    allowedItemTypesDlgError: hasError ? ((dataRes.callStatusMessage || dropdownRes.callStatusMessage || 'Error loading allowed item types')) : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    allowedItemTypesDlgLoading: false,
                    allowedItemTypesDlgError: 'Error loading allowed item types: ' + err.toString(),
                }));
            });
    };

    const closeAllowedItemTypesDialog = () => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: false,
            allowedItemTypesMilestone: null,
            allowedItemTypesDropdownData: [],
            allowedItemTypesSelected: new Set<string>(),
            allowedItemTypesDlgLoading: false,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
        }));
    };

    const handleAllowedItemTypesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, allowedItemTypesDlgFilterText: e.target.value }));
    };

    const toggleAllowedItemTypeSelection = (itemTypeId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.allowedItemTypesSelected);
            if (newSelected.has(itemTypeId)) {
                newSelected.delete(itemTypeId);
            } else {
                newSelected.add(itemTypeId);
            }

            if (prev.allowedItemTypesMilestone) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateSecurityItemType';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        SecurityID: prev.allowedItemTypesMilestone.securityID,
                        selected: prev.allowedItemTypesMilestone.securityID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)).filter(id => Number.isFinite(id)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({ ...s, allowedItemTypesDlgError: 'Error saving: ' + (res.callStatusMessage || '') }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({ ...s, allowedItemTypesDlgError: 'Error saving: ' + err.toString() }));
                    });
            }

            return { ...prev, allowedItemTypesSelected: newSelected };
        });
    };

    const openUserRolesDialog = (milestone: MenuInfoDto) => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: true,
            userRolesMilestone: milestone,
            userRolesDropdownData: [],
            userRolesData: [],
            userRolesDlgLoading: true,
            userRolesDlgError: '',
            userRolesEditingKey: null,
            userRolesOriginalItem: null,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));

        const dataUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetSecurityClientQueriesForDropDown?MilestoneID=${milestone.securityID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetClientQueriesForDropDown`;

        Promise.all([
            fetch(dataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                if (dataRes.callStatus === 'UNAUTH' || dropdownRes.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDlgLoading: false }));
                    return;
                }

                if (dataRes.callStatus !== 'OK' || !Array.isArray(dataRes.results)) {
                    setState(prev => ({
                        ...prev,
                        userRolesDlgLoading: false,
                        userRolesDlgError: dataRes.callStatusMessage || 'Failed to load client queries',
                    }));
                    return;
                }

                const dropdownData: LookupListItem[] = dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)
                    ? dropdownRes.results.map((item: any) => ({
                        value: String(item.value ?? item.id ?? ''),
                        display: String(item.display ?? item.name ?? `Client Query ${item.value ?? item.id ?? ''}`),
                    })).filter((item: LookupListItem) => item.value !== '')
                    : [];

                const rows: UserRoleMenuRow[] = dataRes.results.map((item: any) => {
                    const securityClientQueryId = Number(item.securityClientQueryID ?? item.SecurityClientQueryID ?? item.id ?? item.ID ?? 0);
                    const clientQueryIdNumber = Number(item.clientQueryID ?? item.ClientQueryID ?? 0);
                    const resolvedDisplay = dropdownData.find(d => Number(d.value) === clientQueryIdNumber)?.display
                        || item.display
                        || item.Display
                        || `Client Query ${clientQueryIdNumber}`;
                    return {
                        clientRowKey: securityClientQueryId > 0 ? String(securityClientQueryId) : `cq-${clientQueryIdNumber}-${Math.random().toString(36).slice(2, 8)}`,
                        SecurityClientQueryID: Number.isFinite(securityClientQueryId) ? securityClientQueryId : 0,
                        SecurityID: Number(item.securityID ?? item.SecurityID ?? milestone.securityID),
                        ClientQueryID: Number.isFinite(clientQueryIdNumber) && clientQueryIdNumber > 0 ? clientQueryIdNumber : null,
                        SortOrder: Number(item.sortOrder ?? item.SortOrder ?? 0),
                        MultiSelect: Boolean(item.multiSelect ?? item.MultiSelect ?? false),
                        LastChangedBy: String(item.lastChangedBy ?? item.LastChangedBy ?? ''),
                        LastChangedOn: (item.lastChangedOn ?? item.LastChangedOn) ? new Date(item.lastChangedOn ?? item.LastChangedOn) : null,
                        display: String(resolvedDisplay),
                        inEdit: false,
                    };
                });

                setState(prev => ({
                    ...prev,
                    userRolesDropdownData: dropdownData,
                    userRolesData: rows,
                    userRolesDlgLoading: false,
                    userRolesDlgError: dropdownData.length === 0 && (!dropdownRes || dropdownRes.callStatus !== 'OK')
                        ? (dropdownRes?.callStatusMessage || 'Failed to load client query dropdown')
                        : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    userRolesDlgLoading: false,
                    userRolesDlgError: 'Error loading client queries: ' + err.toString(),
                }));
            });
    };

    const closeUserRolesDialog = () => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: false,
            userRolesMilestone: null,
            userRolesDropdownData: [],
            userRolesData: [],
            userRolesDlgLoading: false,
            userRolesDlgError: '',
            userRolesEditingKey: null,
            userRolesOriginalItem: null,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const openUserRoleMenuDeleteConfirm = (row: UserRoleMenuRow) => {
        setState(prev => ({
            ...prev,
            userRolesDeleteConfirmKey: row.clientRowKey,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const closeUserRoleMenuDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const confirmUserRoleMenuDelete = () => {
        const key = state.userRolesDeleteConfirmKey;
        if (!key) return;

        const row = state.userRolesData.find(r => r.clientRowKey === key);
        if (!row) {
            closeUserRoleMenuDeleteConfirm();
            return;
        }

        if (row.SecurityClientQueryID <= 0) {
            setState(prev => ({
                ...prev,
                userRolesData: prev.userRolesData.filter(r => r.clientRowKey !== key),
                userRolesEditingKey: null,
                userRolesOriginalItem: null,
                userRolesDeleteConfirmKey: null,
                userRolesDeleteInProgress: false,
                userRolesDeleteError: '',
            }));
            return;
        }

        setState(prev => ({ ...prev, userRolesDeleteInProgress: true, userRolesDeleteError: '' }));
        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/DeleteSecurityClientQuery`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(row.SecurityClientQueryID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDeleteInProgress: false }));
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, userRolesDeleteInProgress: false, userRolesDeleteError: 'Error deleting: ' + (res.callStatusMessage || '') }));
                    return;
                }

                setState(prev => ({
                    ...prev,
                    userRolesData: prev.userRolesData.filter(r => r.clientRowKey !== key),
                    userRolesDlgLoading: false,
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                    userRolesDeleteConfirmKey: null,
                    userRolesDeleteInProgress: false,
                    userRolesDeleteError: '',
                }));
            })
            .catch(err => {
                setState(prev => ({ ...prev, userRolesDeleteInProgress: false, userRolesDeleteError: 'Error deleting: ' + err.toString() }));
            });
    };

    const startEditUserRoleMenu = (row: UserRoleMenuRow) => {
        if (state.userRolesEditingKey !== null) return;
        setState(prev => ({
            ...prev,
            userRolesEditingKey: row.clientRowKey,
            userRolesOriginalItem: { ...row },
            userRolesData: prev.userRolesData.map(r => ({ ...r, inEdit: r.clientRowKey === row.clientRowKey })),
            userRolesDlgError: '',
        }));
    };

    const addUserRoleMenu = () => {
        if (!state.userRolesMilestone || state.userRolesEditingKey !== null) return;
        const key = `new-ur-${Date.now()}`;
        const row: UserRoleMenuRow = {
            clientRowKey: key,
            SecurityClientQueryID: 0,
            SecurityID: state.userRolesMilestone.securityID,
            ClientQueryID: null,
            SortOrder: 0,
            MultiSelect: false,
            LastChangedBy: '',
            LastChangedOn: null,
            display: '',
            inEdit: true,
        };
        setState(prev => ({
            ...prev,
            userRolesData: [...prev.userRolesData, row],
            userRolesEditingKey: key,
            userRolesOriginalItem: null,
            userRolesDlgError: '',
        }));
    };

    const cancelEditUserRoleMenu = (row: UserRoleMenuRow) => {
        setState(prev => {
            if (!prev.userRolesOriginalItem || row.SecurityClientQueryID <= 0) {
                return {
                    ...prev,
                    userRolesData: prev.userRolesData
                        .filter(r => r.clientRowKey !== row.clientRowKey || r.SecurityClientQueryID > 0)
                        .map(r => ({ ...r, inEdit: false })),
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                };
            }

            const original = prev.userRolesOriginalItem;
            return {
                ...prev,
                userRolesData: prev.userRolesData.map(r => r.clientRowKey === original.clientRowKey ? { ...original, inEdit: false } : { ...r, inEdit: false }),
                userRolesEditingKey: null,
                userRolesOriginalItem: null,
            };
        });
    };

    const saveUserRoleMenu = (row: UserRoleMenuRow) => {
        if (!state.userRolesMilestone) return;
        if (!row.ClientQueryID) {
            setState(prev => ({ ...prev, userRolesDlgError: 'Please select a Client Query before saving.' }));
            return;
        }
        if (!Number.isInteger(Number(row.SortOrder))) {
            setState(prev => ({ ...prev, userRolesDlgError: 'Sort Order must be a whole number.' }));
            return;
        }

        setState(prev => ({ ...prev, userRolesDlgLoading: true, userRolesDlgError: '' }));

        const payload = {
            SecurityClientQueryID: row.SecurityClientQueryID > 0 ? row.SecurityClientQueryID : 0,
            SecurityID: state.userRolesMilestone.securityID,
            ClientQueryID: row.ClientQueryID,
            SortOrder: Number(row.SortOrder),
            MultiSelect: Boolean(row.MultiSelect),
            LastChangedBy: row.LastChangedBy || props.userInf.displayName || '',
            LastChangedOn: row.LastChangedOn,
        };

        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/AddOrUpdateSecurityClientQuery`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDlgLoading: false }));
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, userRolesDlgLoading: false, userRolesDlgError: 'Error saving: ' + (res.callStatusMessage || '') }));
                    return;
                }

                const newIdRaw = Number(res.stringResponse ?? res.StringResponse ?? row.SecurityClientQueryID ?? 0);
                const newId = Number.isFinite(newIdRaw) ? newIdRaw : row.SecurityClientQueryID;
                setState(prev => ({
                    ...prev,
                    userRolesData: prev.userRolesData.map(r =>
                        r.clientRowKey === row.clientRowKey
                            ? {
                                ...r,
                                clientRowKey: String(newId),
                                SecurityClientQueryID: newId,
                                inEdit: false,
                                display: prev.userRolesDropdownData.find(d => Number(d.value) === r.ClientQueryID)?.display || r.display,
                              }
                            : r
                    ),
                    userRolesDlgLoading: false,
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    userRolesDlgLoading: false,
                    userRolesDlgError: 'Error saving: ' + err.toString(),
                }));
            });
    };

    const handleMenuItemClick = (action: string) => {
        const item = state.menuDataItem;
        if (!item) return;

        switch (action) {
            case 'allowedItemTypes':
                openAllowedItemTypesDialog(item);
                break;
            case 'clientQueries':
                openUserRolesDialog(item);
                break;
        }

        closeRowMenu();
    };

    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                if (top + menuH > scrollY + winH) {
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + 2);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch {
                // ignore measurement errors
            }
        }, 10);

        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (menuRef.current && !menuRef.current.contains(target) && state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        if (state.menuDataItem) {
            document.addEventListener('click', handleDocumentClick);
            return () => document.removeEventListener('click', handleDocumentClick);
        }
    }, [state.menuDataItem, state.menuAnchor]);

    // Shared editable text cell used by the ID/Label/Description/Sort Order/URL/Changed By
    // columns. Memoized with useCallback (reading the EDIT_FIELD flag already embedded on each
    // gridData row instead of closing over `state`) so the GridColumn's `cell` prop keeps the
    // same function identity across renders. Previously this was a plain const re-declared on
    // every render, so every keystroke recreated it and Kendo's Grid remounted the <input>,
    // dropping focus after a single character. The keepEditInputFocus call below was a workaround
    // for that remount and is left in place as a harmless safety net, but should no longer be
    // needed now that the renderer identity is stable.
    const textCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const isEditing = props.dataItem[EDIT_FIELD] === true;
        const editCellId = `${props.dataItem.clientRowKey}::${props.field!}`;
        return (
            <td className="tmi-dialog-grid-text-cell" title={String(val)}>
                {isEditing ? (
                    <input
                        type="text"
                        className="k-input k-input-md"
                        data-edit-cell={editCellId}
                        value={String(val)}
                        disabled={props.field === 'securityID'}
                        onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            const selectionStart = target.selectionStart;
                            const selectionEnd = target.selectionEnd;
                            const updated = { ...props.dataItem, [props.field!]: e.target.value };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                            keepEditInputFocus(updated.clientRowKey, props.field!, selectionStart, selectionEnd);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                ) : val}
            </td>
        );
    }, [keepEditInputFocus]);

    const dateCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!];
        const display = val ? new Date(val).toLocaleString() : '';
        return <td className="tmi-dialog-grid-text-cell" title={display}>{display}</td>;
    };

    const BooleanCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!];
        const isEditing = state.editRowKey === props.dataItem.clientRowKey;
        return (
            <td className="tmi-dialog-grid-center-cell" style={{ cursor: 'pointer' }}>
                {isEditing ? (
                    <Checkbox
                        checked={Boolean(val)}
                        onChange={(e: any) => {
                            const updated = { ...props.dataItem, [props.field!]: Boolean(e.value) };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                    />
                ) : (val ? '✓' : '')}
            </td>
        );
    };

    const MenuTypeDropdownCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const isEditing = state.editRowKey === props.dataItem.clientRowKey;

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={String(val ?? '')}
                        onChange={(e) => {
                            const selected = e.target.value;
                            const updated = { ...props.dataItem, packListTypeID: selected === '' ? null : Number(selected) };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {state.milestoneTypesDropdown.map(mt => (
                            <option key={mt.value} value={mt.value}>{mt.display}</option>
                        ))}
                    </select>
                </td>
            );
        }

        const display = state.milestoneTypesDropdown.find(mt => mt.value === String(val ?? ''))?.display || val;
        return <td className="tmi-dialog-grid-text-cell" title={String(display)}>{display}</td>;
    };

    const ActionTypeDropdownCell = (props: GridCellProps) => {
        const val = props.dataItem.securityActionTypeID;
        const isEditing = state.editRowKey === props.dataItem.clientRowKey;

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={String(val ?? '')}
                        onChange={(e) => {
                            const selected = e.target.value;
                            const selectedNumber = selected === '' ? 0 : Number(selected);
                            const updated = {
                                ...props.dataItem,
                                securityActionTypeID: Number.isFinite(selectedNumber) ? selectedNumber : 0,
                                actionTypeId: selected === '' ? null : (Number.isFinite(selectedNumber) ? selectedNumber : null),
                            };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {state.actionTypesDropdown.map(actionType => (
                            <option key={actionType.value} value={actionType.value}>{actionType.display}</option>
                        ))}
                    </select>
                </td>
            );
        }

        const display = state.actionTypesDropdown.find(actionType => actionType.value === String(val ?? ''))?.display || val;
        return <td className="tmi-dialog-grid-text-cell" title={String(display)}>{display}</td>;
    };

    const IconCodeCell = (props: GridCellProps) => {
        const value = String(props.dataItem.iconCode ?? '');
        const isEditing = state.editRowKey === props.dataItem.clientRowKey;
        const parsedCode = parseIconCode(value);
        const iconEntity = toIconEntity(value);

        return (
            <td className="tmi-dialog-grid-text-cell" title={value || 'No icon selected'} style={{ padding: '2px 4px', overflow: 'visible' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                        dangerouslySetInnerHTML={{ __html: iconEntity || '&#9633;' }}
                        style={{
                            fontSize: '20px',
                            lineHeight: '22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '56px',
                            minWidth: '56px',
                            flexShrink: 0,
                            overflow: 'visible',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            fontFamily: isWebComponentsPrivateUseCode(parsedCode) ? 'WebComponentsIcons' : 'inherit',
                        }}
                    />
                    {isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <input
                                type="text"
                                className="k-input k-input-md"
                                value={value}
                                onChange={(e) => {
                                    const updated = { ...props.dataItem, iconCode: e.target.value };
                                    setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                style={{ width: '100%' }}
                            />
                            <button
                                className="k-button k-button-sm k-rounded-md"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openIconPicker('edit', value);
                                }}
                                title="Pick icon"
                            >
                                Pick
                            </button>
                        </div>
                    ) : (
                        <span style={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{value || 'None'}</span>
                    )}
                </div>
            </td>
        );
    };

    const ParentSecurityDropdownCell = (props: GridCellProps) => {
        const val = props.dataItem.parentSecurityID;
        const isEditing = state.editRowKey === props.dataItem.clientRowKey;
        const currentItem = props.dataItem as MenuInfoDto;

        const options: LookupListItem[] = state.data
            .filter(item => {
                const actionTypeDisplay = state.actionTypesDropdown
                    .find(actionType => Number(actionType.value) === Number(item.securityActionTypeID))
                    ?.display
                    ?.trim()
                    .toLowerCase();
                return actionTypeDisplay === 'menu group header';
            })
            .filter(item => item.securityID !== currentItem.securityID)
            .filter(item => !wouldCreateCircularParentReference(currentItem.securityID, item.securityID, state.data))
            .map(item => ({
                value: String(item.securityID),
                display: String(item.securityLabel || item.description || item.securityID),
            }))
            .sort((a, b) => a.display.localeCompare(b.display));

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={val === null ? '' : String(val)}
                        onChange={(e) => {
                            const selected = e.target.value;
                            const nextParentSecurityId = selected === '' ? null : Number(selected);
                            if (wouldCreateCircularParentReference(currentItem.securityID, nextParentSecurityId, state.data)) {
                                setState(prev => ({ ...prev, errorMsg: 'Parent Security selection would create a circular reference.' }));
                                return;
                            }
                            const updated = {
                                ...props.dataItem,
                                parentSecurityID: nextParentSecurityId,
                            };
                            setState(prev => ({
                                ...prev,
                                errorMsg: '',
                                data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item),
                            }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- None --</option>
                        {options.map(menu => (
                            <option key={menu.value} value={menu.value}>{menu.display}</option>
                        ))}
                    </select>
                </td>
            );
        }

        const parentItem = state.data.find(item => Number(item.securityID) === Number(val ?? 0));
        const display = parentItem
            ? (parentItem.securityLabel || parentItem.description || String(parentItem.securityID))
            : (val ?? '');
        return <td className="tmi-dialog-grid-text-cell" title={String(display)}>{display}</td>;
    };

    const HamburgerHeaderCell = () => (
        <th className="tmi-header-action-cell tmi-command-column">
            <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={fetchData} disabled={state.loading} title="Refresh">
                <span className="k-icon k-font-icon k-i-reload" />
            </button>
        </th>
    );

    const HamburgerCell = (props: GridCellProps) => (
        <td style={{ textAlign: 'center', padding: '2px' }}>
            {state.editRowKey === props.dataItem.clientRowKey ? (
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-success"
                    title="Save"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSave(props.dataItem as MenuInfoDto);
                    }}
                >
                    <span className="k-icon k-font-icon k-i-check" />
                </button>
            ) : (
                state.actionTypesDropdown
                    .find(actionType => Number(actionType.value) === Number((props.dataItem as MenuInfoDto).securityActionTypeID))
                    ?.display
                    ?.trim()
                    .toLowerCase() === 'spl search'
                ? (
                <button
                    className="k-button k-button-sm k-rounded-md"
                    title="Actions"
                    onClick={(e) => {
                        e.stopPropagation();
                        openRowMenu(props.dataItem as MenuInfoDto, e.currentTarget as HTMLElement);
                    }}
                >
                    <span className="k-icon k-font-icon k-i-more-vertical" />
                </button>
                ) : null
            )}
        </td>
    );

    const CommandCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit = state.editRowKey === dataItem.clientRowKey;
        return (
            <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleCancel(); }} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { openDeleteConfirm(dataItem as MenuInfoDto); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleEnterEdit(dataItem as MenuInfoDto); }} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { openDeleteConfirm(dataItem as MenuInfoDto); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    };

    const CommandHeaderCell = () => (
        <th className="tmi-header-action-cell tmi-command-column">
            <div className="tmi-header-action-stack">
                <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-header-action-button tmi-dialog-action-button-first" title={state.showSecondaryColumns ? 'Show Primary Columns' : 'Show Secondary Columns'} onClick={toggleColumnVisibility}>
                    <span className={`k-icon k-font-icon ${state.showSecondaryColumns ? 'k-i-arrow-chevron-left' : 'k-i-arrow-chevron-right'}`} />
                </button>
                <button className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-header-action-button tmi-dialog-action-button-next" title="New Milestone" onClick={openNewDialog}>
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </div>
        </th>
    );

    const buildHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `v2_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field)
                    ? (stateRef.current.sort[0].dir as string)
                    : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort, handleHeaderFocus, handleHeaderBlur, handleHeaderFilterChange]);

    const buildBooleanHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `v2_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">↕</span>
                            </label>
                            <select
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            >
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [handleHeaderFilterChange, handleHeaderFocus, handleHeaderBlur, toggleSort]);

    const createSimpleHeaderCell = useCallback((field: string, title: string) => {
        const cacheKey = `v2_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field)
                    ? (stateRef.current.sort[0].dir as string)
                    : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div
                            onMouseDown={onSimpleHeaderMouseDown}
                            onClick={blockSimpleHeaderClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: '4px 6px',
                                boxSizing: 'border-box',
                                fontSize: '12px',
                                lineHeight: '1.3',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                flex: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort]);

    const filteredData = applyMenuFilters(state.data, state.filterTexts || {});
    const sortedData = orderBy(filteredData, state.sort);

    const visibleData = state.editRowKey && state.editViewOrderKeys
        ? (() => {
            const byKey = new Map(filteredData.map(item => [item.clientRowKey, item] as const));
            const frozen = state.editViewOrderKeys
                .map(key => byKey.get(key))
                .filter((item): item is MenuInfoDto => Boolean(item));

            const frozenKeys = new Set(frozen.map(item => item.clientRowKey));
            const appended = filteredData.filter(item => !frozenKeys.has(item.clientRowKey));
            return [...frozen, ...appended];
        })()
        : sortedData;

    const gridData = visibleData.map(item => ({
        ...item,
        [EDIT_FIELD]: state.editRowKey === item.clientRowKey,
    }));

    stateRef.current = state;

    return (
        <div className="packlists-container">
            {state.errorMsg && <div className="packlists-error">{state.errorMsg}</div>}

            <Grid
                className="tmi-grid PacklistTypes-grid Menus-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey="clientRowKey"
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
                onFilterChange={handleFilterChange}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn field="securityID" title="ID" width={60} hidden={!isColumnVisible('securityID', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('securityID', 'ID')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="securityLabel" title="Label" hidden={!isColumnVisible('securityLabel', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('securityLabel', 'Label')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="parentSecurityID" title="Parent Security" hidden={!isColumnVisible('parentSecurityID', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('parentSecurityID', 'Parent Security')} cell={ParentSecurityDropdownCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="description" title="Description" hidden={!isColumnVisible('description', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('description', 'Description')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="securityActionTypeID" title="Action Type" hidden={!isColumnVisible('securityActionTypeID', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('securityActionTypeID', 'Action Type')} cell={ActionTypeDropdownCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="sortOrder" title="Sort Order" width={85} hidden={!isColumnVisible('sortOrder', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('sortOrder', 'Sort Order')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="url" title="URL" hidden={!isColumnVisible('url', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('url', 'URL')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="packListTypeID" title="PackList Type" hidden={!isColumnVisible('packListTypeID', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('packListTypeID', 'PackList Type')} cell={MenuTypeDropdownCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="iconCode" title="Icon Code" width={220} hidden={!isColumnVisible('iconCode', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('iconCode', 'Icon Code')} cell={IconCodeCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="enable" title="Enabled" width={90} hidden={!isColumnVisible('enable', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('enable', 'Enabled')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="mobileEnable" title="Mobile Enable" width={110} hidden={!isColumnVisible('mobileEnable', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('mobileEnable', 'Mobile Enable')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="tabletEnable" title="Tablet" width={90} hidden={!isColumnVisible('tabletEnable', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('tabletEnable', 'Tablet')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="browserEnable" title="Browser Enable" width={110} hidden={!isColumnVisible('browserEnable', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('browserEnable', 'Browser Enable')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="showEmptyPackList" title="Show Empty" width={95} hidden={!isColumnVisible('showEmptyPackList', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('showEmptyPackList', 'Show Empty')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="showNACells" title="Show NA" width={90} hidden={!isColumnVisible('showNACells', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('showNACells', 'Show NA')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="lastChangedBy" title="Changed By" hidden={!isColumnVisible('lastChangedBy', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('lastChangedBy', 'Changed By')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="lastChangedOn" title="Changed On" hidden={!isColumnVisible('lastChangedOn', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('lastChangedOn', 'Changed On')} cell={dateCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>

            {state.deleteConfirmItem && (
                <Dialog title="Confirm Delete" onClose={closeDeleteConfirm}>
                    <p>Delete Menu '{state.deleteConfirmItem.securityLabel}'?</p>
                    {state.deleteErrorMsg && <div className="packlists-error">{state.deleteErrorMsg}</div>}
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-error" onClick={confirmDelete} disabled={state.deleteInProgress}>Delete</button>
                        <button className="k-button k-button-solid-base" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.newDialogOpen && (
                <Dialog title="New Menu" onClose={closeNewDialog}>
                    <div style={{ padding: '16px', maxHeight: '65vh', overflowY: 'auto' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Identity</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label>Security ID</label>
                                    <input
                                        autoFocus
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogSecurityID}
                                        disabled
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Security Label</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogSecurityLabel}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogSecurityLabel: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogDescription}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogDescription: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>URL</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogURL}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogURL: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Lookup And Numeric</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label>Sort Order *</label>
                                    <input
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogSortOrder}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogSortOrder: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Parent Security</label>
                                    <select
                                        className="k-input k-input-md"
                                        value={state.newDialogParentSecurityID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogParentSecurityID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    >
                                        <option value="">-- None --</option>
                                        {state.data
                                            .filter(item => {
                                                const actionTypeDisplay = state.actionTypesDropdown
                                                    .find(actionType => Number(actionType.value) === Number(item.securityActionTypeID))
                                                    ?.display
                                                    ?.trim()
                                                    .toLowerCase();
                                                return actionTypeDisplay === 'menu group header';
                                            })
                                            .sort((a, b) => {
                                                const aLabel = String(a.securityLabel || a.description || a.securityID).toLowerCase();
                                                const bLabel = String(b.securityLabel || b.description || b.securityID).toLowerCase();
                                                return aLabel.localeCompare(bLabel);
                                            })
                                            .map(item => (
                                                <option key={item.securityID} value={item.securityID}>{item.securityLabel || item.description || item.securityID}</option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label>PackList Type</label>
                                    <select
                                        className="k-input k-input-md"
                                        value={state.newDialogPackListTypeID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogPackListTypeID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    >
                                        <option value="">-- Select --</option>
                                        {state.milestoneTypesDropdown.map(mt => (
                                            <option key={mt.value} value={mt.value}>{mt.display}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>Action Type</label>
                                    <select
                                        className="k-input k-input-md"
                                        value={state.newDialogActionTypeID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogActionTypeID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    >
                                        <option value="">-- Select --</option>
                                        {state.actionTypesDropdown.map(actionType => (
                                            <option key={actionType.value} value={actionType.value}>{actionType.display}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>Icon Code</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                        <input
                                            type="text"
                                            className="k-input k-input-md"
                                            value={state.newDialogIconCode}
                                            onChange={(e) => setState(prev => ({ ...prev, newDialogIconCode: e.target.value }))}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            className="k-button k-button-md k-rounded-md"
                                            onClick={() => openIconPicker('new', state.newDialogIconCode)}
                                        >
                                            Pick
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Options</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                                <Checkbox checked={state.newDialogEnable} onChange={(e: any) => setState(prev => ({ ...prev, newDialogEnable: Boolean(e.value) }))} label="Enable" />
                                <Checkbox checked={state.newDialogMobileEnable} onChange={(e: any) => setState(prev => ({ ...prev, newDialogMobileEnable: Boolean(e.value) }))} label="Mobile Enable" />
                                <Checkbox checked={state.newDialogTabletEnable} onChange={(e: any) => setState(prev => ({ ...prev, newDialogTabletEnable: Boolean(e.value) }))} label="Tablet Enable" />
                                <Checkbox checked={state.newDialogBrowserEnable} onChange={(e: any) => setState(prev => ({ ...prev, newDialogBrowserEnable: Boolean(e.value) }))} label="Browser Enable" />
                                <Checkbox checked={state.newDialogShowEmptyPackList} onChange={(e: any) => setState(prev => ({ ...prev, newDialogShowEmptyPackList: Boolean(e.value) }))} label="Show Empty PackList" />
                                <Checkbox checked={state.newDialogShowNACells} onChange={(e: any) => setState(prev => ({ ...prev, newDialogShowNACells: Boolean(e.value) }))} label="Show NA Cells" />
                            </div>
                        </div>

                        {state.newDialogError && <div className="packlists-error">{state.newDialogError}</div>}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-primary" onClick={submitNew} disabled={state.newDialogSaving}>Create</button>
                        <button className="k-button k-button-solid-base" onClick={closeNewDialog} disabled={state.newDialogSaving}>Cancel</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <IconPickerDialog
                open={state.iconPickerOpen}
                title="Select Menu Icon"
                value={state.iconPickerValue}
                onApply={applyIconPickerSelection}
                onClose={closeIconPicker}
            />

            {state.allowedItemTypesDlgOpen && state.allowedItemTypesMilestone && (
                <Dialog
                    title={`Allowed Item Types For ${state.allowedItemTypesMilestone.description || state.allowedItemTypesMilestone.securityLabel || `ID ${state.allowedItemTypesMilestone.securityID}`}`}
                    onClose={closeAllowedItemTypesDialog}
                    width={500}
                    height={600}
                >
                    <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.allowedItemTypesDlgError && (
                            <div className="tmi-dialog-list-error">
                                {state.allowedItemTypesDlgError}
                            </div>
                        )}
                        {state.allowedItemTypesDlgLoading ? (
                            <div className="tmi-dialog-list-loading">
                                <span className="k-icon k-font-icon k-i-loading" />
                                Loading item types...
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    className="k-input tmi-dialog-list-filter"
                                    placeholder="Filter item types..."
                                    value={state.allowedItemTypesDlgFilterText}
                                    onChange={handleAllowedItemTypesFilterChange}
                                />
                                <div className="tmi-dialog-list-content" style={{ flex: 1, overflowY: 'auto' }}>
                                    {state.allowedItemTypesDropdownData
                                        .filter(itemType => itemType.display.toLowerCase().includes(state.allowedItemTypesDlgFilterText.toLowerCase()))
                                        .map((itemType) => (
                                            <div key={itemType.value} className="tmi-dialog-list-item">
                                                <input
                                                    type="checkbox"
                                                    id={`allowed-item-type-${itemType.value}`}
                                                    checked={state.allowedItemTypesSelected.has(itemType.value)}
                                                    onChange={() => toggleAllowedItemTypeSelection(itemType.value)}
                                                />
                                                <label htmlFor={`allowed-item-type-${itemType.value}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                    {itemType.display}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAllowedItemTypesDialog}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.userRolesDlgOpen && state.userRolesMilestone && (
                <Dialog
                    title={`Client Queries for ${state.userRolesMilestone.description || state.userRolesMilestone.securityLabel || `ID ${state.userRolesMilestone.securityID}`}`}
                    onClose={closeUserRolesDialog}
                    width={700}
                    height={550}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '410px' }}>
                        {state.userRolesDlgError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '12px' }}>
                                {state.userRolesDlgError}
                            </div>
                        )}
                        {state.userRolesDlgLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                                <span style={{ marginLeft: '8px' }}>Loading...</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    {state.userRolesData.length === 0 ? (
                                        <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                                            No client queries assigned
                                        </div>
                                    ) : (
                                        <Grid data={state.userRolesData} className="tmi-editor-grid" dataItemKey="clientRowKey">
                                            <GridColumn
                                                field="display"
                                                title="Client Query"
                                                cell={(cellProps) => {
                                                    return (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {cellProps.dataItem.inEdit ? (
                                                                <select
                                                                    className="k-input k-input-sm"
                                                                    value={cellProps.dataItem.ClientQueryID ?? ''}
                                                                    onChange={(e) => {
                                                                        const selectedRoleId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                        const selectedRole = state.userRolesDropdownData.find(r => Number(r.value) === selectedRoleId);
                                                                        setState(prev => ({
                                                                            ...prev,
                                                                            userRolesData: prev.userRolesData.map(item =>
                                                                                item.clientRowKey === cellProps.dataItem.clientRowKey
                                                                                    ? { ...item, ClientQueryID: selectedRoleId, display: selectedRole?.display || '' }
                                                                                    : item
                                                                            ),
                                                                        }));
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                >
                                                                    <option value="">-- Select a Client Query --</option>
                                                                    {state.userRolesDropdownData
                                                                        .filter(role => {
                                                                            const roleId = Number(role.value);
                                                                            const isCurrent = roleId === cellProps.dataItem.ClientQueryID;
                                                                            const isUsed = state.userRolesData.some(r => r.ClientQueryID === roleId && r.clientRowKey !== cellProps.dataItem.clientRowKey);
                                                                            return isCurrent || !isUsed;
                                                                        })
                                                                        .map(role => (
                                                                            <option key={role.value} value={role.value}>
                                                                                {role.display}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                            ) : (
                                                                cellProps.dataItem.display
                                                            )}
                                                        </td>
                                                    );
                                                }}
                                            />
                                            <GridColumn
                                                title="Sort Order"
                                                width={90}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <input
                                                                type="number"
                                                                className="k-input k-input-sm"
                                                                value={cellProps.dataItem.SortOrder ?? 0}
                                                                onChange={(e) => {
                                                                    const val = Number(e.target.value);
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        userRolesData: prev.userRolesData.map(item =>
                                                                            item.clientRowKey === cellProps.dataItem.clientRowKey
                                                                                ? { ...item, SortOrder: Number.isFinite(val) ? val : 0 }
                                                                                : item
                                                                        ),
                                                                    }));
                                                                }}
                                                                style={{ width: '100%' }}
                                                            />
                                                        ) : (
                                                            String(cellProps.dataItem.SortOrder ?? 0)
                                                        )}
                                                    </td>
                                                )}
                                            />
                                            <GridColumn
                                                title="Multi Select"
                                                width={90}
                                                cell={(cellProps) => (
                                                    <td className="tmi-dialog-grid-center-cell" style={{ cursor: cellProps.dataItem.inEdit ? 'default' : 'pointer' }}>
                                                        <Checkbox
                                                            checked={Boolean(cellProps.dataItem.MultiSelect)}
                                                            disabled={!cellProps.dataItem.inEdit}
                                                            onChange={(e: any) => {
                                                                const val = Boolean(e.value);
                                                                setState(prev => ({
                                                                    ...prev,
                                                                    userRolesData: prev.userRolesData.map(item =>
                                                                        item.clientRowKey === cellProps.dataItem.clientRowKey
                                                                            ? { ...item, MultiSelect: val }
                                                                            : item
                                                                    ),
                                                                }));
                                                            }}
                                                        />
                                                    </td>
                                                )}
                                            />

                                            <GridColumn
                                                title="Actions"
                                                width={90}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '4px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => cancelEditUserRoleMenu(cellProps.dataItem as UserRoleMenuRow)}
                                                                    disabled={state.userRolesDlgLoading}
                                                                    title="Cancel"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-cancel" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => saveUserRoleMenu(cellProps.dataItem as UserRoleMenuRow)}
                                                                    disabled={state.userRolesDlgLoading}
                                                                    title="Save"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-check" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => startEditUserRoleMenu(cellProps.dataItem as UserRoleMenuRow)}
                                                                    disabled={state.userRolesDlgLoading || state.userRolesEditingKey !== null}
                                                                    title="Edit"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-edit" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => openUserRoleMenuDeleteConfirm(cellProps.dataItem as UserRoleMenuRow)}
                                                                    disabled={state.userRolesDlgLoading || state.userRolesEditingKey !== null}
                                                                    title="Delete"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-trash" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </div>
                                <button
                                    disabled={state.userRolesData.some(r => r.inEdit) || state.userRolesDlgLoading}
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                    onClick={addUserRoleMenu}
                                    title="Add new Client Query"
                                    style={{ marginTop: '8px' }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" /> Add Client Query
                                </button>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUserRolesDialog}
                            disabled={state.userRolesDlgLoading}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.userRolesDeleteConfirmKey !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeUserRoleMenuDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this client query assignment?
                    </p>
                    {state.userRolesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.userRolesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmUserRoleMenuDelete}
                            disabled={state.userRolesDeleteInProgress}
                        >
                            {state.userRolesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUserRoleMenuDeleteConfirm}
                            disabled={state.userRolesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.menuDataItem && (
                <div
                    ref={menuRef}
                    onMouseLeave={handleMenuBlur}
                    onMouseEnter={() => {
                        if (menuBlurTimeoutRef.current) window.clearTimeout(menuBlurTimeoutRef.current);
                    }}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition ? `${state.menuPosition.left}px` : '-9999px',
                        top: state.menuPosition ? `${state.menuPosition.top}px` : '-9999px',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 10000,
                        minWidth: '200px',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0', minWidth: '200px' }}>
                        <button
                            type="button"
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '8px 16px',
                                textAlign: 'left',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                fontSize: '14px',
                            }}
                            onClick={() => handleMenuItemClick('allowedItemTypes')}
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                            Allowed Item Types
                        </button>
                        <button
                            type="button"
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '8px 16px',
                                textAlign: 'left',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                fontSize: '14px',
                                borderTop: '1px solid #e0e0e0',
                            }}
                            onClick={() => handleMenuItemClick('clientQueries')}
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                            Client Queries
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default connector(Menus);
